import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useState } from "react";
import { Check, Info } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { Avatar } from "@/modules/escala/components/Avatar";
import { Pill } from "@/modules/escala/components/Pill";
import { useFuncoesMinisterio } from "@/modules/escala/hooks/useEscalas";
import type { ParticipanteDetalhe } from "@/modules/escala/services/escalaService";

interface ModalAlterarFuncaoProps {
  participante: ParticipanteDetalhe;
  ministerioId: string;
  carregando: boolean;
  erro: string | null;
  onConfirmar: (novaFuncaoId: string) => void;
  onCancelar: () => void;
}

/**
 * `escala_alterar_funcao` não mexe em resposta/rodada nem gera notificação
 * (a UI não deve implicar que a pessoa foi avisada — seção 7 do relatório
 * técnico) — só troca a função exercida naquela participação.
 *
 * A página só monta este componente quando há um participante escolhido
 * (assim a lista de funções do ministério não é consultada à toa).
 */
export function ModalAlterarFuncao({
  participante,
  ministerioId,
  carregando,
  erro,
  onConfirmar,
  onCancelar,
}: ModalAlterarFuncaoProps) {
  const { data: funcoes, isLoading, isError } = useFuncoesMinisterio(ministerioId);
  const [funcaoId, setFuncaoId] = useState(participante.funcaoId ?? "");

  const mudou = !!funcaoId && funcaoId !== participante.funcaoId;

  return (
    <Modal titulo="Alterar função" aberto onClose={onCancelar}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-2xl bg-surface-2 p-3">
          <Avatar nome={participante.nome} />
          <div className="min-w-0">
            <p className="break-words text-[15px] font-bold leading-tight text-text">{participante.nome}</p>
            <p className="text-[13px] text-muted">
              Função atual: <span className="font-semibold text-text">{participante.funcaoNome ?? "Sem função"}</span>
            </p>
          </div>
        </div>

        <p className="flex items-start gap-2.5 rounded-xl bg-info/10 px-3.5 py-3 text-[13.5px] leading-snug text-text">
          <Info size={18} aria-hidden className="mt-px shrink-0 text-info" />
          <span>
            Só ajusta a função exercida nesta escala. A resposta já registrada continua valendo e{" "}
            <b>a pessoa não é avisada</b>.
          </span>
        </p>

        {isLoading && <p className="text-sm text-muted">Carregando funções do ministério...</p>}
        {isError && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
            Não foi possível carregar as funções deste ministério.
          </p>
        )}

        {(funcoes ?? []).length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">Nova função</legend>
            {(funcoes ?? []).map((f) => (
              <label
                key={f.id}
                className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl border-2 border-border bg-surface px-3.5 py-2 transition-colors hover:border-muted/60 has-[:checked]:border-primary has-[:checked]:bg-primary/10 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary"
              >
                <input
                  type="radio"
                  name="nova-funcao"
                  value={f.id}
                  checked={funcaoId === f.id}
                  onChange={() => setFuncaoId(f.id)}
                  className="peer sr-only"
                />
                <span
                  aria-hidden
                  className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full border-2 border-border text-transparent transition-colors peer-checked:border-primary peer-checked:bg-primary peer-checked:text-on-primary"
                >
                  <Check size={13} strokeWidth={3} />
                </span>
                <span className="flex-1 text-[15px] font-semibold text-text">{f.nome}</span>
                {f.id === participante.funcaoId && <Pill tom="off">Atual</Pill>}
              </label>
            ))}
          </fieldset>
        )}

        {erro && (
          <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
            {erro}
          </p>
        )}

        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => mudou && onConfirmar(funcaoId)}
            disabled={!mudou || carregando}
            className="min-h-11 w-full rounded-xl bg-primary px-4 text-[15px] font-semibold text-on-primary transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {carregando ? "Salvando..." : mudou ? "Salvar nova função" : "Escolha outra função"}
          </button>
          <BotaoVoltar onClick={onCancelar} disabled={carregando} className="min-h-11 w-full rounded-xl" />
        </div>
      </div>
    </Modal>
  );
}
